import React, { useState } from 'react';
import { Form, Card, Container, Row, Col, Spinner } from 'react-bootstrap';
import supabase from "../Services/Supabase";

const THEMES = [
  { id: 'elegant', name: 'Elegante' },
  { id: 'sofisticato', name: 'Sofisticato' },
  { id: 'alternativo', name: 'Alternativo' },
  { id: 'minimale', name: 'Minimale' },
  { id: 'rustico', name: 'Rustico' },
  { id: 'casereccio', name: 'Casereccio' }
];

// Dimensioni massime per l'ottimizzazione delle immagini
const MAX_WIDTH = 600;
const MAX_HEIGHT = 400;
const WEBP_QUALITY = 0.8;

const CLOUDFLARE_WORKER_URL = 'https://img.jovanni-tidei.workers.dev/';

const MenuHeader = ({ menu, onUpdateMenu, onFileUpload, user, onImageClick }) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // Funzione per convertire l'immagine in WebP e ridimensionarla
  const optimizeImage = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          // Calcola le nuove dimensioni mantenendo le proporzioni
          let width = img.width;
          let height = img.height;
          
          if (width > MAX_WIDTH) {
            const ratio = MAX_WIDTH / width;
            width = MAX_WIDTH;
            height = height * ratio;
          }
          
          if (height > MAX_HEIGHT) {
            const ratio = MAX_HEIGHT / height;
            height = height * ratio;
            width = width * ratio;
          }
          
          // Crea un canvas per il ridimensionamento
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          // Converti in WebP
          canvas.toBlob((blob) => {
            if (blob) {
              // Crea un nuovo File con il tipo WebP
              const optimizedFile = new File([blob], `${file.name.split('.')[0]}.webp`, {
                type: 'image/webp'
              });
              resolve(optimizedFile);
            } else {
              reject(new Error("Conversione in WebP fallita"));
            }
          }, 'image/webp', WEBP_QUALITY);
        };
        img.onerror = () => reject(new Error("Caricamento immagine fallito"));
      };
      reader.onerror = () => reject(new Error("Lettura file fallita"));
    });
  };
  
  // Funzione per eliminare l'immagine precedente
  const deleteOldLogo = async (oldLogoUrl) => {
    if (!oldLogoUrl) return;
    
    try {
      // Estrai il path dell'immagine dall'URL
      const urlParts = oldLogoUrl.split('/');
      const filePath = urlParts[urlParts.length - 1];
      
      if (filePath && user?.id) {
        const path = `${user.id}/${filePath}`;
        
        // Elimina l'immagine da Supabase Storage
        const { error } = await supabase.storage
          .from("logo_menu")
          .remove([path]);
          
        if (error) {
          console.error("Errore durante l'eliminazione dell'immagine precedente:", error);
        }
      }
    } catch (error) {
      console.error("Errore nel processo di eliminazione:", error);
    }
  };
  
  // Gestione dell'upload del logo
  const handleLogoUpload = async (e) => {
    if (!e.target.files || !e.target.files[0]) return;
    
    const file = e.target.files[0];
    setIsUploading(true);
    setUploadProgress(10);
    
    try {
      // Ottimizza l'immagine (WebP + ridimensionamento)
      setUploadProgress(30);
      const optimizedFile = await optimizeImage(file);
      setUploadProgress(50);
      
      // Crea un nome file unico con timestamp
      const timestamp = Date.now();
      const fileExt = 'webp';
      const fileName = `logo_${timestamp}.${fileExt}`;
      const filePath = `${user.id}/${fileName}`;
      
      // Carica l'immagine ottimizzata su Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from("logo_menu")
        .upload(filePath, optimizedFile, {
          cacheControl: '3600',
          upsert: false
        });
      
      setUploadProgress(80);
      
      if (uploadError) {
        throw uploadError;
      }
      
      // Ottieni l'URL pubblico dell'immagine caricata
      const { data } = supabase.storage
        .from("logo_menu")
        .getPublicUrl(filePath);

      // Crea l'URL del worker
      const workerUrl = `${CLOUDFLARE_WORKER_URL}?url=${encodeURIComponent(data.publicUrl)}`;
      
      // Elimina il logo precedente se esiste
      await deleteOldLogo(menu.logo_url);
      
      // Aggiorna lo stato del menu con il nuovo URL del logo
      onUpdateMenu('logo_url', workerUrl);
      setUploadProgress(100);
      
    } catch (error) {
      console.error("Errore durante l'upload:", error);
      alert(`Errore durante l'upload dell'immagine: ${error.message}`);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };
  
  return (
    <Container fluid className="py-4">
      <Row className="mb-4">
        <Col>
          <h1 className="text-center display-4 mb-5">Intestazione</h1>
        </Col>
      </Row>
      
      <Row className="mb-4">
        <Col md={8} className="mx-auto">
          <Card className="shadow-sm rounded-3">
            <Card.Body className="p-4">
              <Form>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-4">
                      <Form.Label className="fw-bold">Logo del Ristorante</Form.Label>
                      <Form.Control
                        type="file"
                        onChange={handleLogoUpload}
                        className="form-control-lg"
                        accept="image/*"
                        disabled={isUploading}
                      />
                      
                      {isUploading && (
                        <div className="mt-3">
                          <div className="d-flex align-items-center">
                            <Spinner animation="border" size="sm" className="me-2" />
                            <span>Ottimizzazione e caricamento... {uploadProgress}%</span>
                          </div>
                          <div className="progress mt-2">
                            <div 
                              className="progress-bar" 
                              role="progressbar" 
                              style={{width: `${uploadProgress}%`}}
                              aria-valuenow={uploadProgress} 
                              aria-valuemin="0" 
                              aria-valuemax="100"
                            ></div>
                          </div>
                        </div>
                      )}
                      
                      {menu.logo_url && !isUploading && (
                        <div className="mt-3 text-center">
                          <img
                            src={menu.logo_url}
                            alt="Logo"
                            className="img-fluid header-image-preview"
                            onClick={onImageClick}
                            style={{ cursor: 'pointer' }}
                          />
                          <p className="text-muted mt-2 small">
                            Formato WebP ottimizzato
                          </p>
                        </div>
                      )}
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-4">
                      <Form.Label className="fw-bold">Nome del Ristorante</Form.Label>
                      <Form.Control
                        type="text"
                        value={menu.name}
                        onChange={(e) => onUpdateMenu('name', e.target.value)}
                        className="form-control-lg"
                        placeholder="Inserisci il nome"
                      />
                    </Form.Group>
                    
                    <Form.Group className="mb-4">
                      <Form.Label className="fw-bold">Sottotitolo</Form.Label>
                      <Form.Control
                        type="text"
                        value={menu.subtitle}
                        onChange={(e) => onUpdateMenu('subtitle', e.target.value)}
                        className="form-control-lg"
                        placeholder="Inserisci un sottotitolo"
                      />
                    </Form.Group>
                    
                    <Form.Group className="mb-4">
                      <Form.Label className="fw-bold">Tema</Form.Label>
                      <Form.Select
                        value={menu.theme}
                        onChange={(e) => onUpdateMenu('theme', e.target.value)}
                        className="form-control-lg"
                      >
                        {THEMES.map(theme => (
                          <option key={theme.id} value={theme.id}>
                            {theme.name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default MenuHeader;